/**
 * DAC profiles -- group 06 (valve-output European houses and the Japanese majors).
 *
 * The through-line of this group is that the converter core and the analogue
 * output stage pull in different directions. Three of the eight put a valve or a
 * transformer after the converter, where the output stage dominates the audible
 * fingerprint; the other five put a very linear buffer after a very linear
 * converter, where the digital front end -- filter choice, noise shaper order,
 * clocking -- is the only thing left that differentiates them.
 *
 * Several of these manufacturers publish no distortion or dynamic-range data at
 * all. Where that is the case the entry is tagged ESTIMATED and the
 * provenanceNote says exactly which numbers are published and which are
 * inferred. Nothing here is claimed as a measurement.
 */

import type { DacProfile } from '../../types/hardware';
import {
  FILTERS,
  HARMONIC_SHAPES,
  TRANSFORMER_LINE,
  TUBE_LINE,
  makeDac,
  usd,
} from '../kit';

export const DACS_06: DacProfile[] = [
  makeDac({
    mfr: 'Wavelength Audio',
    model: 'Cosecant v3',
    price: usd(
      3500,
      'APPROXIMATE',
      2012,
      'Built to order in several trim levels; approximately USD 3,500 for the v3 USB version. Exact figure not verified.',
    ),
    year: 2008,
    country: 'United States',
    architecture: 'ladder-multibit',
    core: 'Non-oversampling multibit ladder converter fed by asynchronous Streamlength USB',
    outputStage: 'tube',
    dynamicRangeDb: 106,
    thdPlusNDb: -70,
    crosstalkDb: -74,
    outputVrmsSE: 2.2,
    outputImpedanceOhms: 1500,
    balanced: false,
    sampleRates: [44100, 48000, 88200, 96000],
    maxBitDepth: 24,
    dsd: 'none',
    filters: [FILTERS.nos()],
    defaultFilterId: 'nos',
    ladderMismatchLsb: 0.95,
    effectiveBits: 17,
    jitterPsRms: 50,
    harmonicShape: HARMONIC_SHAPES.tubeDac,
    response: TUBE_LINE(13, -1.5),
    slewRateVus: 8,
    confidence: 'ESTIMATED',
    accent: '#c8842f',
    tags: ['nos', 'usb-async', 'no-feedback', 'single-ended'],
    notes:
      'Asynchronous USB feeding a non-oversampling ladder with a single directly-heated triode gain stage and no global feedback. Two mechanisms dominate the model: the zero-order hold, which imposes the full sin(x)/x droop of nearly 4 dB at Nyquist at 44.1 kHz and leaves the first image family unfiltered above Nyquist, and a source impedance near 1.5 kilohm, which forms a divider with whatever input stage follows and rolls the top octave off further into a low-impedance load. Distortion is second-harmonic dominated and grows roughly 1 dB per dB of drive rather than sitting on a fixed floor, so linearity degrades on loud passages instead of on quiet ones.',
    provenanceNote:
      'Architecture, the asynchronous USB implementation and the valve output are from Wavelength published material. Wavelength publishes no THD, dynamic range, crosstalk or output impedance figures; every electrical number in this entry is ESTIMATED from the class of no-feedback triode-output non-oversampling designs. Ladder element mismatch and jitter are MODELLED.',
  }),

  makeDac({
    mfr: 'Ayon Audio',
    model: 'Stratos',
    price: usd(
      12000,
      'APPROXIMATE',
      2023,
      'Approximately USD 12,000. Ayon pricing varies by market and configuration; exact figure not verified.',
    ),
    year: 2019,
    country: 'Austria',
    architecture: 'delta-sigma',
    core: 'Dual-differential delta-sigma converter with valve output and integral analogue volume control',
    outputStage: 'tube',
    dynamicRangeDb: 118,
    thdPlusNDb: -92,
    crosstalkDb: -96,
    outputVrmsSE: 2.5,
    outputVrmsBal: 5.0,
    outputImpedanceOhms: 300,
    balanced: true,
    dsd: 'dsd256',
    filters: [FILTERS.sharpLinear(128), FILTERS.slowMinimum(32)],
    jitterPsRms: 38,
    noiseShaperOrder: 5,
    effectiveBits: 19,
    harmonicShape: HARMONIC_SHAPES.tubeDac,
    response: TUBE_LINE(6, -0.5),
    slewRateVus: 22,
    confidence: 'ESTIMATED',
    accent: '#a8574a',
    tags: ['valve-output', 'direct-drive', 'oversampling', 'volume-control'],
    notes:
      'Oversampling delta-sigma conversion into a valve line stage that also carries the volume control, so the unit is intended to drive a power amplifier without a preamplifier in between. Unlike the non-oversampling entry above it, all anti-imaging is done in the digital domain, so the analogue response is flat to beyond 20 kHz and the rate-dependent droop is absent; what colours the output is the buffer, which sits roughly 20 dB above an op-amp stage in second-harmonic content and presents a few hundred ohms of source impedance instead of a few tens. Because the volume control is analogue and ahead of the valve, distortion at low listening levels falls rather than staying constant the way a digital-domain attenuator would leave it.',
    provenanceNote:
      'Valve output topology and the direct-drive volume implementation are from Ayon published descriptions. The converter part number is not stated in accessible material, so the core is described generically. THD+N, dynamic range, crosstalk and output impedance are ESTIMATED; Ayon does not publish distortion figures for this model.',
  }),

  makeDac({
    mfr: 'Jadis',
    model: 'JS1 MkV',
    price: usd(
      25000,
      'APPROXIMATE',
      2023,
      'Approximately USD 25,000. Jadis pricing is set regionally and is not consistently published; exact figure not verified.',
    ),
    year: 2014,
    country: 'France',
    architecture: 'r2r',
    core: 'Multibit R-2R ladder conversion into a valve gain stage and wound output transformers',
    outputStage: 'transformer',
    dynamicRangeDb: 104,
    thdPlusNDb: -66,
    crosstalkDb: -68,
    outputVrmsSE: 3.0,
    outputVrmsBal: 6.0,
    outputImpedanceOhms: 220,
    balanced: true,
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000],
    maxBitDepth: 24,
    dsd: 'none',
    filters: [FILTERS.nos(), FILTERS.sharpLinear(128)],
    defaultFilterId: 'nos',
    ladderMismatchLsb: 1.15,
    effectiveBits: 16,
    jitterPsRms: 110,
    response: TRANSFORMER_LINE(14, 32000),
    slewRateVus: 6,
    confidence: 'ESTIMATED',
    accent: '#9c7b3f',
    tags: ['ladder', 'transformer-coupled', 'valve', 'nos'],
    notes:
      'Multibit ladder conversion into a valve gain stage feeding wound output transformers. The transformer is the dominant modelled element: a first-order low-frequency corner in the mid-teens of hertz, a roll-off starting around 32 kHz, and core hysteresis that makes distortion rise as frequency falls instead of staying flat with frequency the way a direct-coupled stage does. Superimposed on that is ladder integral nonlinearity, which raises distortion as level falls. The combination gives a residue that is worst at low frequencies and at low signal levels, which is the exact inverse of the delta-sigma entries in this group and is why the two architectures cannot be equalised into each other.',
    provenanceNote:
      'Jadis publishes essentially no electrical specification for the JS1: no THD, no dynamic range, no output impedance, no crosstalk. The multibit ladder converter and the valve output stage are from the manufacturer product description. The transformer-coupled output modelled here is an INFERENCE from the Jadis practice of winding its own transformers and is not a published fact. Every electrical figure in this entry is ESTIMATED.',
  }),

  makeDac({
    mfr: 'Accuphase',
    model: 'DC-901',
    price: usd(
      27000,
      'APPROXIMATE',
      2011,
      'US retail was approximately USD 27,000 at introduction. Exact figure not verified.',
    ),
    year: 2011,
    country: 'Japan',
    architecture: 'multibit-delta-sigma',
    core: 'MDS++ : eight delta-sigma converter elements per channel, current-summed',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 120,
    thdPlusNDb: -104,
    crosstalkDb: -118,
    outputVrmsSE: 2.5,
    outputVrmsBal: 2.5,
    outputImpedanceOhms: 50,
    balanced: true,
    sampleRates: [44100, 48000, 88200, 96000, 176400, 192000],
    maxBitDepth: 24,
    dsd: 'dsd64',
    filters: [FILTERS.sharpLinear(128), FILTERS.slowLinear(48)],
    parallelUnits: 8,
    noiseShaperOrder: 5,
    idleToneLevel: 0.04,
    jitterPsRms: 15,
    internalOversampling: 8,
    confidence: 'ESTIMATED',
    accent: '#c9b28a',
    tags: ['parallel-converters', 'mds', 'discrete-iv', 'japanese-flagship'],
    notes:
      'MDS++ sums eight delta-sigma converter elements per channel. Converter noise and modulator idle tones are uncorrelated between elements while signal is correlated, so paralleling eight of them buys roughly 9 dB of noise floor and averages the idle-tone content down toward nothing; the model carries that as a very low broadband floor with an idle-tone tendency set an order of magnitude below the single-converter entries. Distortion stays essentially flat with level rather than rising with drive, which is the opposite of the ladder and valve designs here. The discrete Class A current-to-voltage and buffer stage holds source impedance low enough that nothing downstream reshapes the response.',
    provenanceNote:
      'Accuphase publishes complete electrical specifications for the DC-901 and describes the MDS++ paralleling scheme, including the element count. The dynamic range, THD+N, crosstalk and output level given here are recalled approximations of those published values rather than transcriptions, so this entry is tagged ESTIMATED rather than MANUFACTURER_SPEC. Idle-tone level and jitter are MODELLED consequences of the paralleling scheme.',
  }),

  makeDac({
    mfr: 'Luxman',
    model: 'D-10X',
    price: usd(
      17000,
      'APPROXIMATE',
      2021,
      'US retail in the high teens of thousands of dollars. Exact figure not verified.',
    ),
    year: 2020,
    country: 'Japan',
    architecture: 'delta-sigma',
    core: 'ROHM BD34301EKV 32-bit delta-sigma, one converter per channel, dual-mono supplies',
    outputStage: 'jfet',
    dynamicRangeDb: 123,
    thdPlusNDb: -103,
    crosstalkDb: -114,
    outputVrmsSE: 2.5,
    outputVrmsBal: 2.5,
    outputImpedanceOhms: 200,
    balanced: true,
    dsd: 'dsd512',
    filters: [FILTERS.sharpLinear(128), FILTERS.slowLinear(48)],
    noiseShaperOrder: 6,
    jitterPsRms: 24,
    internalOversampling: 8,
    confidence: 'ESTIMATED',
    accent: '#7f8fa6',
    tags: ['rohm', 'dual-mono', 'filter-switchable', 'sacd-transport'],
    notes:
      'A single ROHM converter per channel with fully separated dual-mono analogue supplies and a discrete JFET-input buffer. The differentiating parameter in this profile is not the converter but the filter pair: the two selectable roll-offs have almost identical magnitude response inside the audio band and differ mainly in impulse behaviour, one linear-phase with symmetric pre and post ringing over 128 taps and one slow roll-off that trades about 65 dB of stopband rejection for a much shorter impulse. The JFET buffer gives a Class-A-like harmonic structure, second harmonic first with fast decay above the third, rather than the odd-order-tilted residue a feedback op-amp stage leaves behind.',
    provenanceNote:
      'Converter part, dual-mono layout, DSD support and the two-position filter switch are from Luxman published material. Dynamic range, THD+N, crosstalk and output impedance are recalled approximations of Luxman published figures and are tagged ESTIMATED. The JFET-input output buffer is an INFERENCE from the Luxman emphasis on discrete analogue circuitry, not a confirmed part list.',
  }),

  makeDac({
    mfr: 'Marantz',
    model: 'SA-10',
    price: usd(
      6999,
      'ORIGINAL_RETAIL',
      2016,
      'US launch price. The model has been repriced since introduction; current retail differs.',
    ),
    year: 2016,
    country: 'Japan',
    architecture: 'hybrid',
    core: 'Marantz Musical Mastering: all inputs converted to an 11.2 MHz 1-bit stream in DSP, then to analogue by a discrete filter network',
    outputStage: 'passive-iv',
    dynamicRangeDb: 114,
    thdPlusNDb: -94,
    crosstalkDb: -105,
    outputVrmsSE: 2.4,
    outputVrmsBal: 4.8,
    outputImpedanceOhms: 100,
    balanced: true,
    dsd: 'dsd256',
    filters: [FILTERS.slowMinimum(32), FILTERS.slowLinear(48), FILTERS.sharpLinear(128)],
    defaultFilterId: 'slow-min',
    noiseShaperOrder: 7,
    idleToneLevel: 0.28,
    internalOversampling: 256,
    jitterPsRms: 30,
    nonlinear: { kind: 'sigma-delta', amount: 0.34, asymmetry: 0.06, headroomDb: 12 },
    confidence: 'ESTIMATED',
    accent: '#5f7f9a',
    tags: ['no-dac-chip', 'dsd-conversion', 'discrete-filter', 'one-bit'],
    notes:
      'There is no conventional converter chip in the signal path: every input, PCM or DSD, is turned into an 11.2 MHz 1-bit stream in the Marantz Musical Mastering DSP and then into analogue by a discrete filter network. All of the reconstruction burden therefore falls on a very high-order noise shaper, which the model carries as a seventh-order shaper with an in-band floor that is low but rises steeply above 20 kHz, plus a nonzero idle-tone tendency that no multibit design in this group has. The analogue filter is deliberately gentle, so that shaped ultrasonic noise reaches the output instead of being brickwalled away, and any nonlinearity further down the chain can fold it back into the audio band.',
    provenanceNote:
      "The conversion architecture -- upconversion to 11.2 MHz DSD followed by a discrete analogue filter, with no off-the-shelf converter chip -- is from Marantz published material. The schema stage 'passive-iv' is used here to represent that discrete passive network; Marantz does not describe it as an I/V stage, and the mapping is ours. THD+N, dynamic range and crosstalk are recalled approximations of published figures and are tagged ESTIMATED. Noise shaper order and idle-tone level are MODELLED.",
  }),

  makeDac({
    mfr: 'Technics',
    model: 'SL-G700M2',
    price: usd(3499, 'CURRENT_RETAIL', 2024, 'US list price; verify against current dealer pricing.'),
    year: 2023,
    country: 'Japan',
    architecture: 'delta-sigma',
    core: 'Dual delta-sigma converter fed by the JENO Engine jitter-reduction and noise-shaping LSI',
    outputStage: 'op-amp',
    dynamicRangeDb: 117,
    thdPlusNDb: -98,
    crosstalkDb: -108,
    outputVrmsSE: 2.2,
    outputVrmsBal: 2.2,
    outputImpedanceOhms: 100,
    balanced: true,
    dsd: 'dsd512',
    filters: [FILTERS.sharpLinear(128), FILTERS.slowMinimum(32)],
    noiseShaperOrder: 7,
    jitterPsRms: 10,
    internalOversampling: 8,
    confidence: 'ESTIMATED',
    accent: '#6fa8a0',
    tags: ['jeno-engine', 'battery-clock', 'isolated-digital', 'network-player'],
    notes:
      'The converter itself is an ordinary dual delta-sigma pair; what Technics changes sits in front of it. The JENO Engine re-clocks and re-shapes the incoming stream against a battery-driven low-phase-noise oscillator, and the digital board is galvanically isolated from the analogue board so that supply-borne noise cannot arrive at the output correlated between channels. In this model that shows up as a jitter figure around 10 ps RMS, which puts clock sidebands below the 16-bit dither floor and makes them inaudible in the simulation, and as a noise profile with near-zero interchannel correlation. It is the entry in this group where the digital front end, not the analogue stage, is the entire story.',
    provenanceNote:
      'The JENO Engine, the battery-driven clock and the isolated digital and analogue sections are from Technics published material. The converter part number is not stated in accessible material, so the core is described generically. Dynamic range, THD+N and crosstalk are ESTIMATED, and the 10 ps jitter figure is a MODELLED consequence of the published clocking scheme, not a measurement.',
  }),

  makeDac({
    mfr: 'TEAC',
    model: 'UD-701N',
    price: usd(
      3999,
      'APPROXIMATE',
      2022,
      'US retail approximately USD 3,999 at introduction. Exact figure not verified.',
    ),
    year: 2021,
    country: 'Japan',
    architecture: 'fpga-custom',
    core: 'TEAC discrete Delta-Sigma: FPGA-generated bitstream driving a discrete switching network, four per channel',
    outputStage: 'discrete-class-a',
    dynamicRangeDb: 115,
    thdPlusNDb: -96,
    crosstalkDb: -102,
    outputVrmsSE: 2.4,
    outputVrmsBal: 4.8,
    outputImpedanceOhms: 20,
    balanced: true,
    dsd: 'dsd512',
    filters: [
      FILTERS.sharpLinear(128),
      FILTERS.slowLinear(48),
      FILTERS.sharpMinimum(96),
      FILTERS.slowMinimum(32),
      FILTERS.nos(),
    ],
    parallelUnits: 4,
    noiseShaperOrder: 5,
    jitterPsRms: 18,
    internalOversampling: 64,
    confidence: 'MANUFACTURER_SPEC',
    accent: '#4f6f8f',
    tags: ['discrete-delta-sigma', 'dual-mono', 'filter-menu', 'fpga'],
    notes:
      'TEAC replaces the converter chip with a discrete delta-sigma: an FPGA generates the modulated bitstream and drives a discrete switching network, with separate networks per phase in a fully balanced dual-mono layout. Because the switching elements are discrete and matched by selection rather than trimmed on a die, the residual distortion sits well above a modern monolithic converter, but it is dominated by low orders and does not collapse at low level. The filter menu is unusually wide and includes a bypass position, so the same profile can be run either fully band-limited or with the zero-order-hold image family left in place, which makes it the useful control for filter-audibility comparisons in this group.',
    provenanceNote:
      'The discrete Delta-Sigma architecture, the dual-mono layout, the filter menu including the bypass position, and the published THD+N (0.0015 percent) and signal-to-noise (115 dB) figures, together with the 2.4 V RCA and 4.8 V XLR output levels, are from TEAC published specifications and are tagged MANUFACTURER_SPEC. Jitter, oversampling ratio and the harmonic distribution are MODELLED.',
  }),
];
